import React, { useState, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { MdUpload, MdLogout, MdDownload, MdRefresh, MdCloudUpload } from "react-icons/md";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";
import pythonApiClient from "../../services/api/pythonBaseApi";
import { rrhhApiConfig } from "../../utils/rrhhProyectoHelpers";

const CargaMasivaEmpleados = ({ show, onClose, onSaved, proyectoId }) => {
  const fileInputRef = useRef(null);

  const [file,                 setFile]                = useState(null);
  const [fileForRevalidation,  setFileForRevalidation] = useState(null);
  const [isLoading,            setIsLoading]           = useState(false);
  const [isValidating,         setIsValidating]        = useState(false);
  const [progress,             setProgress]            = useState(0);
  const [results,              setResults]             = useState(null);
  const [fileValidationErrors, setFileValidationErrors]= useState([]);
  const [isFileValid,          setIsFileValid]         = useState(false);

  const showToast = (type, message) =>
    toast[type]?.(message, { position: "bottom-right", autoClose: 3500 });

  // ── Template ────────────────────────────────────────────────────────────
  const downloadTemplate = () => {
    const data = [{
      'DNI':                     '12345678',
      'NOMBRE 1':                'EJEMPLO',
      'NOMBRE 2':                '',
      'APELLIDO 1':              'APELLIDO',
      'APELLIDO 2':              '',
      'GÉNERO':                  'M',
      'FECHA DE NACIMIENTO':     '1990-05-20',
      'RÉGIMEN LABORAL':         '14X7',
      'FECHA INICIO DE LABORES': '2024-01-15',
      'CARGO':                   'ADMINISTRADOR',
      'TELÉFONO':                '987654321',
      'DIRECCIÓN':               'Jr. Ejemplo 123',
      'CORREO':                  'ejemplo@correo.com',
      'BANCO':                   'BCP',
      'N° CUENTA':               '193-12345678-0-12',
      'CCI':                     '00219312345678012314',
      'FONDO':                   'AFP',
      'AFP':                     'INTEGRA',
      'CUSSP':                   '123456ABCDE1',
      'TIPO COMISIÓN':           'FLUJO',
    }];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "template_empleados.xlsx");
    showToast("success", "Template descargado");
  };

  // ── Manejo de archivo ────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      showToast("error", "Por favor seleccione un archivo Excel válido (.xlsx o .xls)");
      e.target.value = '';
      return;
    }

    setFileForRevalidation(selectedFile);
    setIsValidating(true);
    setFileValidationErrors([]);
    setIsFileValid(false);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const res = await pythonApiClient.post(
        "/rrhh/carga-masiva-empleados/validate",
        formData,
        rrhhApiConfig(proyectoId, { "Content-Type": "multipart/form-data" })
      );

      if (res.data?.success === false) {
        setFileValidationErrors(res.data.errores_detallados || []);
        setIsFileValid(false);
        showToast("error", res.data.message || "El archivo tiene errores de validación");
      } else {
        setFile(selectedFile);
        setIsFileValid(true);
        showToast("success", `"${selectedFile.name}" validado — ${res.data.message}`);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Error al validar";
      setFileValidationErrors([{ fila: 1, errores: [msg] }]);
      setIsFileValid(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      showToast("error", msg);
    } finally {
      setIsValidating(false);
    }
  };

  // ── Envío ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { showToast("error", "Por favor seleccione un archivo Excel"); return; }

    setIsLoading(true);
    setProgress(0);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      setProgress(25);
      const res = await pythonApiClient.post(
        "/rrhh/carga-masiva-empleados/",
        formData,
        rrhhApiConfig(proyectoId, { "Content-Type": "multipart/form-data" })
      );
      setProgress(75);

      const r = res.data?.resultados || res.data;
      if (r?.success === false) {
        setResults({
          total: r.total_procesados || 0,
          nuevos: 0, actualizados: 0,
          errors: r.errores_detallados?.length || 0,
          details: r.errores_detallados || [],
        });
        showToast("error", "Errores en la carga. Revise los detalles.");
      } else {
        setResults({
          total:       r.total_procesados    || 0,
          nuevos:      r.nuevos_insertados   || 0,
          actualizados:r.existentes          || 0,
          errors:      r.errores             || 0,
          details:     r.detalles_errores    || [],
        });
        showToast("success", "Carga masiva completada exitosamente");
        if (onSaved) onSaved();
      }
      setProgress(100);
    } catch (err) {
      const data = err.response?.data;
      if (data?.errores_detallados) {
        setFileValidationErrors(data.errores_detallados);
        showToast("error", data.message || "Errores de validación");
      } else {
        showToast("error", data?.detail || "Error de conexión");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const resetForm = () => {
    setFile(null); setFileForRevalidation(null);
    setResults(null); setProgress(0);
    setFileValidationErrors([]); setIsFileValid(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    showToast("info", "Formulario reiniciado");
  };

  // ── Error table helper ────────────────────────────────────────────────────
  const ErrorTable = ({ errores }) => (
    <div className="table-responsive mt-3">
      <table className="dh-error-table table mb-0">
        <thead className="table-dark">
          <tr>
            <th style={{ width: 110, backgroundColor: '#343a40', color: '#fff' }}>📍 Fila Excel</th>
            <th style={{ backgroundColor: '#343a40', color: '#fff' }}>🚫 Errores de Validación</th>
          </tr>
        </thead>
        <tbody>
          {errores.map((err, idx) => (
            <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#f8f9fa' : '#fff' }}>
              <td className="fw-bold text-center">
                <span className="badge bg-danger fs-6">{err.fila}</span>
              </td>
              <td>
                <ul className="list-unstyled mb-0">
                  {(Array.isArray(err.errores) ? err.errores : [err.errores]).map((e, i) => (
                    <li key={i} className="mb-1" style={{ color: '#dc3545' }}>
                      <i className="fas fa-times-circle text-danger me-2" />
                      {e}
                    </li>
                  ))}
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ── JSX ───────────────────────────────────────────────────────────────────
  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} tabIndex="-1">
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content" style={{ borderRadius: "20px", border: "none", overflow: "hidden", minHeight: "90vh" }}>
      <style>{`
        .dh-card-header { background:linear-gradient(135deg,#667eea,#764ba2); color:#fff; padding:1.5rem 2rem; border:none; }
        .dh-header-title { color:#fff; font-weight:700; font-size:1.5rem; margin:0; }
        .btn-dh-primary { background:linear-gradient(135deg,#11998e,#38ef7d); border:none; color:#fff; border-radius:12px; padding:10px 16px; font-weight:600; box-shadow:0 4px 14px rgba(17,153,142,.3); transition:all .3s; cursor:pointer; }
        .btn-dh-primary:hover { background:linear-gradient(135deg,#38ef7d,#2dd4bf); transform:translateY(-2px); color:#fff; }
        .btn-dh-warning { background:linear-gradient(135deg,#f093fb,#f5576c); border:none; color:#fff; border-radius:12px; padding:10px 16px; font-weight:600; box-shadow:0 4px 14px rgba(240,147,251,.3); transition:all .3s; cursor:pointer; }
        .btn-dh-warning:hover { background:linear-gradient(135deg,#f5576c,#e91e63); transform:translateY(-2px); color:#fff; }
        .btn-dh-secondary { background:linear-gradient(135deg,#6b7280,#374151); border:none; color:#fff; border-radius:12px; padding:10px 16px; font-weight:600; box-shadow:0 4px 14px rgba(107,114,128,.3); transition:all .3s; cursor:pointer; }
        .btn-dh-secondary:hover { background:linear-gradient(135deg,#374151,#1f2937); transform:translateY(-2px); color:#fff; }
        .dh-upload-section { background:linear-gradient(135deg,#f0fdf4,#dcfce7); border:2px dashed #16a34a; border-radius:15px; padding:2.5rem; text-align:center; transition:all .3s; margin:1.5rem 0; }
        .dh-upload-section:hover { border-color:#15803d; transform:translateY(-2px); }
        .dh-upload-section.has-file { border-color:#059669; background:linear-gradient(135deg,#ecfdf5,#d1fae5); box-shadow:0 8px 25px rgba(16,185,129,.1); }
        .upload-icon { color:#16a34a; margin-bottom:1rem; }
        .upload-text { color:#374151; font-size:1.1rem; font-weight:600; margin-bottom:.5rem; }
        .upload-subtext { color:#6b7280; font-size:.9rem; margin-bottom:1.5rem; }
        .file-selected-display { background:linear-gradient(135deg,#059669,#047857); color:#fff; padding:12px 20px; border-radius:12px; font-weight:600; display:inline-flex; align-items:center; justify-content:center; margin:.5rem; font-size:1rem; box-shadow:0 4px 14px rgba(5,150,105,.3); border:2px solid #10b981; min-width:180px; height:44px; }
        .btn-file-uniform { padding:12px 20px!important; border-radius:12px!important; font-weight:600!important; font-size:1rem!important; min-width:180px!important; height:44px!important; display:inline-flex!important; align-items:center!important; justify-content:center!important; margin:.5rem!important; transition:all .3s!important; box-shadow:0 4px 14px rgba(0,0,0,.2)!important; }
        .btn-submit-dh { background:linear-gradient(135deg,#667eea,#764ba2); border:none; color:#fff; border-radius:12px; padding:14px 24px; font-weight:700; font-size:1.1rem; box-shadow:0 6px 20px rgba(102,126,234,.3); transition:all .3s; width:100%; margin-top:1.5rem; cursor:pointer; }
        .btn-submit-dh:hover:not(:disabled) { background:linear-gradient(135deg,#764ba2,#8b5fbf); transform:translateY(-2px); color:#fff; }
        .btn-submit-dh:disabled { background:linear-gradient(135deg,#9ca3af,#6b7280); cursor:not-allowed; }
        .dh-progress-section { background:linear-gradient(135deg,#f0fdf4,#dcfce7); border:2px solid #16a34a; border-radius:15px; padding:2rem; margin-top:2rem; }
        .progress-dh { height:12px; border-radius:12px; background:#e5e7eb; overflow:hidden; margin:1rem 0; }
        .progress-bar-dh { background:linear-gradient(135deg,#16a34a,#15803d); border-radius:12px; transition:width .5s; }
        .dh-results-card { background:linear-gradient(135deg,#f0fdf4,#dcfce7); border:2px solid #16a34a; border-radius:20px; margin-top:2rem; overflow:hidden; box-shadow:0 10px 25px rgba(16,185,129,.1); }
        .dh-results-header { background:linear-gradient(135deg,#16a34a,#15803d); color:#fff; padding:1.5rem 2rem; }
        .dh-results-body { padding:2rem; }
        .dh-stat-card { background:#fff; border-radius:15px; padding:1.5rem; text-align:center; box-shadow:0 4px 15px rgba(0,0,0,.05); border:2px solid transparent; transition:all .3s; }
        .dh-stat-card:hover { transform:translateY(-3px); border-color:#667eea; }
        .stat-number { font-size:2.5rem; font-weight:800; margin-bottom:.5rem; }
        .stat-label { font-size:.9rem; font-weight:600; color:#6b7280; text-transform:uppercase; letter-spacing:.05em; }
        .dh-error-table { background:#fff; border-radius:12px; overflow:hidden; box-shadow:0 4px 15px rgba(0,0,0,.05); }
        .dh-error-table thead th { color:#fff; font-weight:700; padding:1rem; border:none; }
        .dh-error-table tbody td { padding:1rem; border:none; border-bottom:1px solid #f3f4f6; vertical-align:top; }
        .dh-error-table tbody tr:hover { background:#fef2f2!important; }
      `}</style>
      
            {/* Header */}
            <div className="dh-card-header">
              <div className="d-flex align-items-center w-100 flex-wrap gap-3">
                <h5 className="dh-header-title">Carga Masiva de Empleados</h5>
                <div className="d-flex gap-2 ms-auto flex-wrap">
                  <button type="button" className="btn-dh-primary d-flex align-items-center gap-2" onClick={downloadTemplate}>
                    <MdDownload size={20} /> Descargar Template
                  </button>
                  <button type="button" className="btn-dh-warning d-flex align-items-center gap-2" onClick={resetForm} disabled={isLoading}>
                    <MdRefresh size={20} /> Limpiar
                  </button>
                  <button className="btn-dh-secondary d-flex align-items-center gap-2" onClick={onClose}>
                    <MdLogout size={20} /> Cerrar
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-body" style={{ padding: '2rem', backgroundColor: '#f8f9fa' }}>
              <form onSubmit={handleSubmit}>

                {/* Sección Formato */}
                <div className="row">
                  <div className="col-12">
                    <label className="form-label fw-bold mb-2" style={{ fontSize: '1rem', color: '#374151' }}>
                      Archivo Excel <span className="text-danger">*</span>
                    </label>

                    <div className="alert mb-3" style={{
                      backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff',
                      borderRadius: '8px', padding: '1rem'
                    }}>
                      <strong>📋 Formato del archivo Excel:</strong>
                      <p className="mb-2 mt-2">El archivo debe tener las siguientes columnas en este orden exacto:</p>
                      <div className="row" style={{ fontSize: '0.85rem' }}>
                        <div className="col-md-6">
                          <ol style={{ paddingLeft: '1.5rem', marginBottom: 0 }}>
                            <li><strong>DNI</strong> <span className="text-danger">(requerido)</span></li>
                            <li><strong>NOMBRE 1</strong> <span className="text-danger">(requerido)</span></li>
                            <li><strong>NOMBRE 2</strong> <span className="text-muted">(opcional)</span></li>
                            <li><strong>APELLIDO 1</strong> <span className="text-danger">(requerido)</span></li>
                            <li><strong>APELLIDO 2</strong> <span className="text-muted">(opcional)</span></li>
                            <li><strong>GÉNERO</strong> <span className="text-danger">(requerido: M / F)</span></li>
                            <li><strong>FECHA DE NACIMIENTO</strong> <span className="text-muted">(opcional: YYYY-MM-DD o DD/MM/YYYY)</span></li>
                            <li><strong>RÉGIMEN LABORAL</strong> <span className="text-danger">(requerido: ej. 14X7)</span></li>
                            <li><strong>FECHA INICIO DE LABORES</strong> <span className="text-muted">(opcional: YYYY-MM-DD o DD/MM/YYYY)</span></li>
                            <li><strong>CARGO</strong> <span className="text-muted">(opcional)</span></li>
                          </ol>
                        </div>
                        <div className="col-md-6">
                          <ol start="11" style={{ paddingLeft: '1.5rem', marginBottom: 0 }}>
                            <li><strong>TELÉFONO</strong> <span className="text-muted">(opcional)</span></li>
                            <li><strong>DIRECCIÓN</strong> <span className="text-muted">(opcional)</span></li>
                            <li><strong>CORREO</strong> <span className="text-muted">(opcional)</span></li>
                            <li><strong>BANCO</strong> <span className="text-danger">(requerido: ej. BBVA)</span></li>
                            <li><strong>N° CUENTA</strong> <span className="text-danger">(requerido)</span></li>
                            <li><strong>CCI</strong> <span className="text-danger">(requerido)</span></li>
                            <li><strong>FONDO</strong> <span className="text-danger">(requerido: AFP / ONP)</span></li>
                            <li><strong>AFP</strong> <span className="text-primary">(dependiente)</span> [Si es AFP]</li>
                            <li><strong>CUSSP</strong> <span className="text-primary">(dependiente)</span> [Si es AFP]</li>
                            <li><strong>TIPO COMISIÓN</strong> <span className="text-primary">(dependiente: FLUJO / MIXTA)</span> [Si es AFP]</li>
                          </ol>
                        </div>
                      </div>
                      <div className="mt-3" style={{
                        backgroundColor: '#fff3cd', border: '1px solid #ffc107',
                        borderRadius: '8px', padding: '0.75rem'
                      }}>
                        <small>
                          <strong>Importante:</strong> Los nombres de las columnas deben ser exactos (case-insensitive).
                          El <strong>Estado</strong> se asignará automáticamente como <strong>Activo</strong>.
                          Si la <strong>Fecha de Inicio</strong> está vacía, se usará la fecha del día de hoy.
                          Use el template para asegurar el formato correcto.
                        </small>
                      </div>
                    </div>

                    {/* Zona de carga */}
                    <div className={`dh-upload-section ${file ? 'has-file' : ''}`}>
                      <MdCloudUpload size={64} className="upload-icon" />
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        disabled={isLoading || !!file}
                        style={{ display: 'none' }}
                      />
                      {!file ? (
                        <>
                          <div className="upload-text">Seleccione su archivo Excel</div>
                          <div className="upload-subtext">Formatos soportados: .xlsx, .xls</div>
                          <label
                            onClick={() => fileInputRef.current?.click()}
                            className="btn-dh-primary btn-file-uniform"
                            style={{ cursor: (isLoading || isValidating) ? 'not-allowed' : 'pointer' }}
                          >
                            {isValidating ? (
                              <><span className="spinner-border spinner-border-sm me-2" />Validando…</>
                            ) : (
                              <><MdUpload size={20} className="me-2" />Examinar Archivos</>
                            )}
                          </label>
                        </>
                      ) : (
                        <>
                          <div className="upload-text">
                            {isFileValid ? "✅ Archivo validado correctamente" : "Archivo seleccionado"}
                          </div>
                          <div className="upload-subtext"><strong>Archivo cargado:</strong></div>
                          <div className="d-flex flex-column align-items-center">
                            <div className="file-selected-display">{file.name}</div>
                            <button
                              type="button"
                              className="btn-dh-warning btn-file-uniform"
                              onClick={() => {
                                setFile(null); setFileForRevalidation(null);
                                setResults(null); setFileValidationErrors([]); setIsFileValid(false);
                                if (fileInputRef.current) fileInputRef.current.value = '';
                              }}
                              disabled={isLoading || isValidating}
                            >
                              Cambiar Archivo
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Errores de validación */}
                {fileValidationErrors.length > 0 && (
                  <div className="mt-4">
                    <div className="alert alert-danger">
                      <h6 className="alert-heading fw-bold">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        ⚠️ Errores de Validación del Archivo Excel
                      </h6>
                      <p className="mb-3">
                        <strong>El archivo no puede ser procesado.</strong> Corrija los errores antes de continuar.
                      </p>
                      <ErrorTable errores={fileValidationErrors} />
                    </div>
                  </div>
                )}

                {/* Botón de envío */}
                <button
                  type="submit"
                  className="btn-submit-dh d-flex align-items-center justify-content-center gap-2"
                  disabled={isLoading || isValidating || !file || !isFileValid}
                >
                  {isLoading ? (
                    <><span className="spinner-border spinner-border-sm" />Procesando archivo…</>
                  ) : isValidating ? (
                    <><span className="spinner-border spinner-border-sm" />Validando archivo…</>
                  ) : (
                    <><MdUpload size={24} />Cargar Empleados</>
                  )}
                </button>
              </form>

              {/* Progreso */}
              {isLoading && (
                <div className="dh-progress-section">
                  <h6 className="fw-bold mb-3 text-center">
                    <i className="fas fa-cog fa-spin me-2" />Procesando archivo…
                  </h6>
                  <div className="progress-dh">
                    <div className="progress-bar-dh" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="text-center"><span className="fw-bold">{progress}% completado</span></div>
                </div>
              )}

              {/* Resultados (Como CargaMasivaComensales) */}
              {results && (
                <div className="dh-results-card">
                  <div className="dh-results-header">
                    <h5 className="mb-0 fw-bold">
                      <i className="fas fa-chart-bar me-2"></i>
                      Resultados de la Carga
                    </h5>
                  </div>
                  <div className="dh-results-body">
                    {/* Primera fila - Total */}
                    <div className="row mb-3">
                      <div className="col-md-12">
                        <div className="dh-stat-card" style={{backgroundColor: '#f8f9fa', border: '2px solid #dee2e6'}}>
                          <div className="stat-number text-primary" style={{fontSize: '3rem'}}>{results.total}</div>
                          <div className="stat-label" style={{fontSize: '1.1rem', fontWeight: 'bold'}}>TOTAL PROCESADOS</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Segunda fila - Detalles */}
                    <div className="row">
                      <div className="col-md-4 mb-4">
                        <div className="dh-stat-card">
                          <div className="stat-number text-success">{results.nuevos}</div>
                          <div className="stat-label">Empleados Nuevos</div>
                        </div>
                      </div>
                      <div className="col-md-4 mb-4">
                        <div className="dh-stat-card">
                          <div className="stat-number text-info">{results.actualizados}</div>
                          <div className="stat-label">Empleados Actualizados</div>
                        </div>
                      </div>
                      <div className="col-md-4 mb-4">
                        <div className="dh-stat-card">
                          <div className="stat-number text-danger">{results.errors}</div>
                          <div className="stat-label">Errores</div>
                        </div>
                      </div>
                    </div>

                    {/* Detalles de errores */}
                    {(results.details && results.details.length > 0) && (
                      <div className="mt-4">
                        <div className="alert alert-danger">
                          <h6 className="alert-heading fw-bold">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Errores Encontrados ({results.details.length} fila{results.details.length !== 1 ? 's' : ''} con problemas)
                          </h6>
                          <p className="mb-3">
                            <strong>⚠️ Carga Cancelada o Incompleta:</strong> Hubo problemas durante la inserción.
                          </p>
                          <ErrorTable errores={results.details} />
                        </div>
                      </div>
                    )}
                    
                    {/* Fallback: Mostrar errores simples */}
                    {results.errors > 0 && (!results.details || results.details.length === 0) && (
                      <div className="mt-4">
                        <div className="alert alert-warning">
                          <h6 className="alert-heading fw-bold">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Errores Encontrados
                          </h6>
                          <p className="mb-0">
                            <strong>Total de errores:</strong> {results.errors}
                          </p>
                          <p className="mb-0">
                            <strong>Mensaje:</strong> Por favor, revise el archivo Excel y corrija los errores antes de volver a cargar.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
        </div>
      </div>
    </div>
  );
};

export default CargaMasivaEmpleados;
